import { GetShareSettingsResponse, PatchShareSettingsType } from '@orbit/api-types'
import api from '@lib/api'
import { queryKeys } from '@lib/queryKeys'
import {
	MutationKey,
	QueryClient,
	UseMutationOptions,
	useMutation,
	useQueryClient,
} from '@tanstack/react-query'
import { useState } from 'react'

export type ShareSettingsFlagKey = keyof GetShareSettingsResponse

/** Snapshot of ONLY the flags this patch touched, captured in `onMutate`. */
export interface ShareSettingsMutationContext {
	previous: Partial<GetShareSettingsResponse> | undefined
}

/**
 * Shared mutation key so concurrent share-settings mutations for the same trip
 * can see each other via `queryClient.isMutating`.
 */
export const updateShareSettingsMutationKey = (tripId: string): MutationKey => [
	...queryKeys.shareSettings(tripId),
	'update',
]

/** Pure set arithmetic behind the hook's `savingKeys` state (kept out of React for testability). */
export const withPatchKeysAdded = (
	keys: ReadonlySet<ShareSettingsFlagKey>,
	patch: PatchShareSettingsType,
): ReadonlySet<ShareSettingsFlagKey> => {
	const next = new Set(keys)
	for (const flag of Object.keys(patch) as ShareSettingsFlagKey[]) next.add(flag)
	return next
}

export const withPatchKeysRemoved = (
	keys: ReadonlySet<ShareSettingsFlagKey>,
	patch: PatchShareSettingsType,
): ReadonlySet<ShareSettingsFlagKey> => {
	const next = new Set(keys)
	for (const flag of Object.keys(patch) as ShareSettingsFlagKey[]) next.delete(flag)
	return next
}

/**
 * Mutation options for patching a trip's sharing flags (ADR 0003). Toggles can
 * overlap, so nothing here may assume it is the only mutation in flight:
 *
 * - `onMutate` snapshots only its own patch's flags; `onError` merges that back
 *   into the CURRENT cache, so a failing toggle does not rewind a sibling.
 * - `onSuccess`/`onSettled` adopt the server object and invalidate only when no
 *   sibling is still in flight (`isMutating > 1` counts the settler itself).
 * - `trip`/`trips` invalidate off the settling patch's own keys, so a master
 *   patch still refreshes the mirror while a section patch is in flight.
 *
 * A factory over an explicit `QueryClient`, so all of this is testable through
 * a bare `MutationObserver` without mounting React.
 */
export const buildUpdateShareSettingsOptions = (
	queryClient: QueryClient,
	tripId: string,
): UseMutationOptions<
	GetShareSettingsResponse,
	Error,
	PatchShareSettingsType,
	ShareSettingsMutationContext
> => {
	const key = queryKeys.shareSettings(tripId)
	const mutationKey = updateShareSettingsMutationKey(tripId)

	return {
		mutationKey,
		mutationFn: async patch => {
			const response = await api.patch<GetShareSettingsResponse>(
				`/trips/${tripId}/share-settings`,
				patch,
			)
			return response.data
		},
		onMutate: async patch => {
			// Stop any in-flight refetch from clobbering the optimistic value.
			await queryClient.cancelQueries({ queryKey: key })
			const current = queryClient.getQueryData<GetShareSettingsResponse>(key)
			if (!current) return { previous: undefined }

			const previous: Partial<GetShareSettingsResponse> = {}
			for (const flag of Object.keys(patch) as ShareSettingsFlagKey[]) {
				previous[flag] = current[flag]
			}
			queryClient.setQueryData<GetShareSettingsResponse>(key, { ...current, ...patch })
			return { previous }
		},
		onError: (_error, _patch, context) => {
			const previous = context?.previous
			if (!previous) return
			queryClient.setQueryData<GetShareSettingsResponse>(key, current =>
				current ? { ...current, ...previous } : current,
			)
		},
		onSuccess: settings => {
			if (queryClient.isMutating({ mutationKey }) > 1) return
			queryClient.setQueryData(key, settings)
		},
		onSettled: (_data, _error, patch) => {
			if ('isVisibleToPublic' in patch) {
				queryClient.invalidateQueries({ queryKey: queryKeys.trip(tripId) })
				queryClient.invalidateQueries({ queryKey: queryKeys.trips() })
			}
			if (queryClient.isMutating({ mutationKey }) > 1) return
			queryClient.invalidateQueries({ queryKey: key })
		},
	}
}

/**
 * Also exposes `savingKeys` — the flags with a PATCH in flight — so the UI can
 * lock rows individually. `variables`/`isPending` track only the LATEST
 * `mutate()` call and would mislock overlapping toggles.
 */
export const useUpdateShareSettings = (tripId: string) => {
	const queryClient = useQueryClient()
	const [savingKeys, setSavingKeys] = useState<ReadonlySet<ShareSettingsFlagKey>>(new Set())
	const { onMutate, onSettled, ...options } = buildUpdateShareSettingsOptions(queryClient, tripId)

	const mutation = useMutation<
		GetShareSettingsResponse,
		Error,
		PatchShareSettingsType,
		ShareSettingsMutationContext
	>({
		...options,
		onMutate: (patch, context) => {
			setSavingKeys(prev => withPatchKeysAdded(prev, patch))
			return onMutate ? onMutate(patch, context) : { previous: undefined }
		},
		onSettled: (data, error, patch, onMutateResult, context) => {
			setSavingKeys(prev => withPatchKeysRemoved(prev, patch))
			return onSettled?.(data, error, patch, onMutateResult, context)
		},
	})

	return { ...mutation, savingKeys }
}
